import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { devtools } from 'zustand/middleware';
import {
  BeamSection,
  BeamSectionState,
  BeamSectionStore,
} from '../types/beamSection';
import { addData } from '../services/centralized';

const initialState: BeamSectionState = {
  sections: {},
  beamSectionMap: {},
  isVisible: true, // Sections visible by default
};

/**
 * Beam Section Store
 * Manages sections (cuboids and cylinders) applied to beams
 */
export const useBeamSectionStore = create<BeamSectionStore>()(
  devtools(
    immer((set, get) => ({
      ...initialState,

      setSection: (sectionId: string, sectionData: Omit<BeamSection, 'id'>) => {
        set((state) => {
          const now = Date.now();
          
          // Remove only conflicting beams from OTHER sections, not this one
          sectionData.beamIds.forEach((beamId: string) => {
            const existingSectionId = state.beamSectionMap[beamId];
            // Only remove if it's a DIFFERENT section ID
            if (existingSectionId && existingSectionId !== sectionId) {
              const oldSection = state.sections[existingSectionId];
              if (oldSection) {
                // Remove this beam from the old section's beam list
                oldSection.beamIds = oldSection.beamIds.filter(bid => bid !== beamId);
                
                // If old section has no more beams, delete it completely
                if (oldSection.beamIds.length === 0) {
                  delete state.sections[existingSectionId];
                  console.log('🗑️ Removed empty section:', existingSectionId);
                }
              }
            }
          });

          const section: BeamSection = {
            id: sectionId,
            ...sectionData,
            updatedAt: now,
          };

          // Store the new section
          state.sections[sectionId] = section;

          // Update beam-to-section mapping
          sectionData.beamIds.forEach((beamId: string) => {
            state.beamSectionMap[beamId] = sectionId;
          });

          console.log('✅ Section saved:', sectionId, {
            beamCount: sectionData.beamIds.length,
            totalSections: Object.keys(state.sections).length
          });
        });
      },

      removeSection: (sectionId: string) => {
        set((state) => {
          const section = state.sections[sectionId];
          if (section) {
            // Remove beam mappings
            section.beamIds.forEach((beamId: string) => {
              delete state.beamSectionMap[beamId];
            });

            // Remove section
            delete state.sections[sectionId];

            console.log('🗑️ Section removed:', sectionId);
          }
        });
      },

      removeSectionsForBeams: (beamIds: string[]) => {
        set((state) => {
          beamIds.forEach((beamId) => {
            const sectionId = state.beamSectionMap[beamId];
            if (sectionId) {
              const section = state.sections[sectionId];
              if (section) {
                // Remove all beam mappings for this section
                section.beamIds.forEach((bid: string) => {
                  delete state.beamSectionMap[bid];
                });
                // Remove the section
                delete state.sections[sectionId];
                console.log('🗑️ Section removed for beam:', beamId);
              }
            }
          });
        });
      },

      getSectionForBeam: (beamId: string) => {
        const sectionId = get().beamSectionMap[beamId];
        if (sectionId) {
          return get().sections[sectionId] || null;
        }
        return null;
      },

      getAllSections: () => {
        return Object.values(get().sections);
      },

      clearAllSections: () => {
        set((state) => {
          state.sections = {};
          state.beamSectionMap = {};
          console.log('🗑️ All sections cleared');
        });
      },

      setVisible: (visible: boolean) => {
        set((state) => {
          state.isVisible = visible;
          console.log('👁️ Sections visibility:', visible ? 'ON' : 'OFF');
        });
      },

      toggleVisible: () => {
        set((state) => {
          state.isVisible = !state.isVisible;
          console.log('👁️ Sections visibility toggled:', state.isVisible ? 'ON' : 'OFF');
        });
      },
    })),
    {
      name: 'beam-section-store',
    }
  )
);

// Subscribe to BeamSectionStore and publish to centralized store
useBeamSectionStore.subscribe((state) => {
  addData("beamSections", state.sections);
  console.log(
    "✅ [BeamSectionStore→Centralized] Published sections:",
    Object.keys(state.sections).length
  );
});
